import { useEffect, useState } from 'react'
import { avisoDoSync } from '@domain/finance'
import type { SyncHealth } from '@domain/finance'
import { dayAndMonth } from '@shared/date/civil'
import { civilDateOf } from '@shared/date/timezone'
import { useFinanceRepositories } from '@ui/finance/finance-context'

/**
 * A faixa de aviso do sync, no topo de TODAS as telas — pedido do dono.
 *
 * ## Por que existe
 *
 * De 11 a 18/10/2026 a conexão do banco no Meu Pluggy parou, e o app seguiu
 * mostrando os números como se estivessem em dia: o sync terminava em "item
 * sincronizado", sem erro, e só não trazia nada novo. A pessoa descobriu por
 * conta própria, uma semana depois. Ver `Docs/07-Integracoes/Pluggy.md`,
 * item 4.
 *
 * ## Em todas as telas, e sem botão de fechar
 *
 * O problema afeta todo número do app, e a faixa reflete um ESTADO real: ela
 * some sozinha na primeira rodada do sync em que os dados voltam a andar
 * (`observeHealth` é ao vivo). Um "fechar" a esconderia com o problema de pé.
 *
 * ## Amarelo
 *
 * É a cor de "falta alguém agir" (`DESIGN.md`, a Regra do Trabalho Único) — e
 * aqui quem age é a pessoa: o item do conector 200 não aceita atualização pela
 * API, e só a reconexão no Meu Pluggy destrava.
 */

const MEU_PLUGGY = 'https://meu.pluggy.ai'

/** O dia da CASA, e não o UTC: 23:19 UTC de 11/10 é 20:19 de 11/10 em Curitiba. */
function dia(iso: string | null): string | null {
  return iso === null ? null : dayAndMonth(civilDateOf(new Date(iso)))
}

function textoDo(aviso: SyncHealth): string {
  if (aviso.status === 'error') {
    const desde = dia(aviso.problemSince)
    return desde === null
      ? '⚠️ A sincronização está falhando. Os números podem estar desatualizados.'
      : `⚠️ A sincronização está falhando desde ${desde}. Os números podem estar desatualizados.`
  }
  // **Atualizar, e não reconectar** (decisão 315): o espelho que o Porquinho
  // lê é preso a UMA conexão do Meu Pluggy. "Refazer a conexão" — o texto da
  // primeira versão — criou uma segunda conexão, em dia, que nunca chegou
  // aqui; o que destravou foi o botão Atualizar na conexão que já existia.
  const ate = dia(aviso.dataUpTo)
  const oQueFazer =
    'No Meu Pluggy, use Atualizar na conexão do banco — uma conexão nova não chega ao Porquinho.'
  return ate === null
    ? `⚠️ Suas transações pararam de chegar. ${oQueFazer}`
    : `⚠️ Suas transações não chegam desde ${ate}. ${oQueFazer}`
}

export function AvisoDeSync() {
  const { providerAccountRepository } = useFinanceRepositories()
  const [itens, setItens] = useState<SyncHealth[]>([])

  useEffect(
    () =>
      providerAccountRepository.observeHealth(setItens, (erro: unknown) => {
        // Sem aviso, e não com um aviso inventado: a falha de LER a saúde não
        // diz nada sobre a saúde. Fica no console para quem for investigar.
        console.error('AvisoDeSync: a assinatura da saúde do sync falhou', erro)
      }),
    [providerAccountRepository],
  )

  const aviso = avisoDoSync(itens)
  if (aviso === null) return null

  return (
    <div
      role="alert"
      className="mb-4 rounded-2xl border px-4 py-3 text-sm"
      style={{
        borderColor: 'color-mix(in oklab, var(--pendente) 45%, transparent)',
        background: 'color-mix(in oklab, var(--pendente) 10%, transparent)',
      }}
    >
      <p>{textoDo(aviso)}</p>
      {aviso.status === 'stale' && (
        <a
          href={MEU_PLUGGY}
          target="_blank"
          rel="noreferrer"
          className="alvo -ml-1 justify-start px-1 font-medium text-[var(--brand)] underline underline-offset-2"
        >
          Abrir o Meu Pluggy
        </a>
      )}
    </div>
  )
}
